import React from 'react';
import styled from 'styled-components';
import {
  LegalPageLayout,
  LegalPageTitle,
  LegalPageUpdated,
  LegalSection,
  LegalHeading,
  LegalBody,
} from '../../shared/layouts/LegalPageLayout';

export interface HelpCenterPageProps {
  onBack: () => void;
}

const Question = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 0.4rem;
`;

const QAItem = styled.div`
  margin-bottom: 1.4rem;
`;

const ContactCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const ContactRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.9rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactLabel = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ContactValue = styled.a`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const FAQ_GROUPS: Array<{ topic: string; items: Array<{ q: string; a: string }> }> = [
  {
    topic: 'Getting started',
    items: [
      {
        q: 'How do I set up my exporter account?',
        a: 'Register with your business details and trade credentials (IEC, GST, AD code). Once verified, you can create your first order or list on the marketplace.',
      },
      {
        q: 'Can buyers use Tvikla for free?',
        a: 'Yes. Buyer accounts are free — you can browse verified exporters, message suppliers, and track orders and shipments at no cost.',
      },
    ],
  },
  {
    topic: 'Orders & production',
    items: [
      {
        q: 'Where do I see production status?',
        a: 'Open an order from your Dashboard or the Shipments page — production status updates from Start to Dispatch alongside the vessel booking calendar.',
      },
      {
        q: 'Can I edit an order after it is created?',
        a: 'Buyer and exporter terms are locked once an order is created. To change quantities, pricing, or terms, raise the change with the other party through Messaging, then create an amended order.',
      },
    ],
  },
  {
    topic: 'Logistics & tracking',
    items: [
      {
        q: 'How is my shipment tracked?',
        a: 'Container and shipment tracking runs live off ULIP, so you see the same status the government logistics network sees — no phone calls to the carrier needed.',
      },
      {
        q: 'Is my cargo insured automatically?',
        a: 'Insurance is arranged per shipment before it leaves the yard; you can see coverage status on the order timeline.',
      },
    ],
  },
  {
    topic: 'Customs & compliance',
    items: [
      {
        q: 'How does Tvikla handle customs filing?',
        a: 'Shipping bills are filed through ICEGATE and tracked through to the Let Export Order (LEO) directly from the order.',
      },
      {
        q: 'Who is responsible if the filed details are wrong?',
        a: 'Tvikla helps you file and track, but the exporter of record is responsible for the accuracy of goods, valuation, and compliance details submitted — see the Terms of Service for details.',
      },
    ],
  },
  {
    topic: 'Payments',
    items: [
      {
        q: 'How do payments and settlement work?',
        a: 'Collections are reconciled against each order, and eBRC filing happens as part of closing the ledger — see the Payments tab for status on any order.',
      },
      {
        q: 'Does Tvikla store my bank details?',
        a: 'No. Payments are processed by our regulated banking and payment partners; Tvikla does not store full bank account or card numbers.',
      },
    ],
  },
  {
    topic: 'Account',
    items: [
      {
        q: 'How do I change my password or business details?',
        a: 'Go to Profile Settings or Business Settings from the account menu to update these at any time.',
      },
      {
        q: 'How do I close my account?',
        a: 'Contact support below — we will confirm any orders in progress before closing the account.',
      },
    ],
  },
];

export const HelpCenterPage: React.FC<HelpCenterPageProps> = ({ onBack }) => {
  return (
    <LegalPageLayout onBack={onBack}>
      <LegalPageTitle>Help Center</LegalPageTitle>
      <LegalPageUpdated>Answers to common questions about production, logistics, customs, payments and your account.</LegalPageUpdated>

      {FAQ_GROUPS.map((group) => (
        <LegalSection key={group.topic}>
          <LegalHeading>{group.topic}</LegalHeading>
          {group.items.map((item) => (
            <QAItem key={item.q}>
              <Question>{item.q}</Question>
              <LegalBody>{item.a}</LegalBody>
            </QAItem>
          ))}
        </LegalSection>
      ))}

      <LegalSection>
        <LegalHeading>Still need help?</LegalHeading>
        <LegalBody>Reach the Tvikla support team directly:</LegalBody>
        <ContactCard>
          <ContactRow>
            <ContactLabel>Email</ContactLabel>
            <ContactValue href="mailto:support@tvikla.com">support@tvikla.com</ContactValue>
          </ContactRow>
          <ContactRow>
            <ContactLabel>In-app</ContactLabel>
            <ContactValue as="span">Use Messaging from your Dashboard for order-specific questions.</ContactValue>
          </ContactRow>
        </ContactCard>
      </LegalSection>
    </LegalPageLayout>
  );
};

export default HelpCenterPage;
